import React, { useEffect, useState } from 'react'
import { Platform, View, ViewStyle } from 'react-native'
import { observer } from 'mobx-react-lite'
import { useTimer } from 'react-timer-hook'
import { DateTime } from 'luxon'

import { color } from '../../theme'
import { Text } from '../text'
import { SvgIcon } from '../svg-icon'
import { iconClock } from '../../assets/icons'
import { useTranslation } from 'react-i18next'

interface DigitProps {
  value: number
}

const Digit = observer((props: any) => {
  const { value } = props
  const leftDigit = value >= 10 ? value.toString()[0] : '0'
  const rightDigit = value >= 10 ? value.toString()[1] : value.toString()

  return (
    <View style={{ display: 'flex', flexDirection: 'row', padding: 0 }}>
      <Text style={{ color: color.white }} variant="body2Emphasized" text={leftDigit} />
      <Text style={{ color: color.white }} variant="body2Emphasized" text={rightDigit} />
    </View>
  )
})

interface CountdownTimerProps {
  date: string
  contest?: boolean
  hideBorder?: boolean
  style?: ViewStyle
  locale?: string
  variant?: 'default' | 'subscription'
  onExpire?: () => void
}

export const CountdownTimer = observer((props: CountdownTimerProps) => {
  const { date, onExpire, style, hideBorder, contest, locale, variant = 'default' } = props

  const datetime = new Date(date)

  const formatted =
    variant === 'subscription'
      ? DateTime.fromISO(date)
          .setLocale(locale ?? 'en')
          .toLocaleString({ month: 'short', day: 'numeric', year: 'numeric' })
      : DateTime.fromISO(date)
          .setLocale(locale ?? 'en')
          .toLocaleString({ month: 'short', day: 'numeric' })

  const { t } = useTranslation()
  const overdueString = t('Common.overdue') ?? ''

  const [endedMessage, setEndedMessage] = useState(overdueString)
  const [clockColor, setClockColor] = useState(color.white)

  const { days, seconds, minutes, hours } = useTimer({
    expiryTimestamp: datetime,
    autoStart: true,
    onExpire: onExpire,
  })

  useEffect(() => {
    if (days === 0 && hours === 0 && minutes === 0 && seconds === 0) {
      if (contest) {
        setEndedMessage(t('Contests.ended', { date: formatted }))
        setBackgroundColor(color.purple)
      } else {
        setBackgroundColor(color.red)
      }
      setClockColor(color.white)
    } else if (minutes < 1 && hours <= 0 && days <= 0) {
      setClockColor(color.red)
    } else if (hours < 1 && days <= 0) {
      setClockColor(color.yellow)
    }
  }, [seconds])

  const [backgroundColor, setBackgroundColor] = useState(color.shade70)

  const isExpired = days === 0 && hours === 0 && minutes === 0 && seconds === 0

  if (variant === 'subscription' && isExpired) {
    return null
  }

  return (
    <View
      style={{
        borderWidth: hideBorder ? 0 : 2,
        backgroundColor,
        ...CONTAINER,
        ...style,
      }}
    >
      {variant === 'subscription' ? (
        <View
          style={{
            height: 8,
            width: 8,
            borderRadius: 100,
            backgroundColor:
              days === 0 && hours === 0 && minutes === 0 && seconds === 0
                ? color.red
                : days <= 0 && hours < 1
                  ? color.red
                  : days <= 1
                    ? color.yellow
                    : color.green,
            marginRight: 8,
          }}
        />
      ) : (
        <View style={{ marginRight: 8 }}>
          {Platform.OS === 'web' ? (
            <svg width="13" height="14" viewBox="0 0 13 14" xmlns="http://www.w3.org/2000/svg">
              <path
                fill={clockColor}
                d="M9.00883 8.87425L6.94015 7.56068V4.58545C6.94015 4.32273 6.74371 4.11256 6.49817 4.11256H6.46134C6.2158 4.11256 6.01937 4.32273 6.01937 4.58545V7.68547C6.01937 7.91535 6.12986 8.13209 6.32016 8.25031L8.56071 9.68867C8.76942 9.82003 9.03952 9.75435 9.16229 9.53104C9.2912 9.30116 9.22367 9.00561 9.00883 8.87425V8.87425ZM12.2684 2.14877L10.3777 0.467404C10.1199 0.237529 9.73317 0.270368 9.51219 0.552786C9.29734 0.828635 9.33417 1.24241 9.59199 1.47885L11.4765 3.16022C11.7343 3.3901 12.1211 3.35726 12.342 3.07484C12.563 2.79899 12.5262 2.38522 12.2684 2.14877V2.14877ZM1.78379 3.16022L3.66832 1.47885C3.93227 1.24241 3.9691 0.828635 3.74812 0.552786C3.53327 0.270368 3.14654 0.237529 2.88873 0.467404L0.998063 2.14877C0.740245 2.38522 0.703414 2.79899 0.924401 3.07484C1.13925 3.35726 1.52598 3.3901 1.78379 3.16022ZM6.63322 1.48542C3.58238 1.48542 1.10856 4.13227 1.10856 7.39649C1.10856 10.6607 3.58238 13.3076 6.63322 13.3076C9.68406 13.3076 12.1579 10.6607 12.1579 7.39649C12.1579 4.13227 9.68406 1.48542 6.63322 1.48542ZM6.63322 11.994C4.26375 11.994 2.33626 9.93168 2.33626 7.39649C2.33626 4.8613 4.26375 2.79899 6.63322 2.79899C9.00269 2.79899 10.9302 4.8613 10.9302 7.39649C10.9302 9.93168 9.00269 11.994 6.63322 11.994Z"
              />
            </svg>
          ) : (
            <SvgIcon size={14} color={clockColor} src={iconClock} />
          )}
        </View>
      )}
      {variant === 'subscription' && days > 30 ? (
        <Text style={{ color: color.white }} text={formatted} variant="body2Emphasized" />
      ) : variant !== 'subscription' && days > 7 ? (
        <Text
          style={{ color: color.white }}
          tx={contest ? 'Common.ends' : 'Common.due'}
          txOptions={{ date: formatted }}
          variant="body2Emphasized"
        />
      ) : days >= 1 ? (
        <Text
          style={{ color: color.white }}
          tx="Common.daysLeft"
          txOptions={{ count: days }}
          variant="body2Emphasized"
        />
      ) : (
        <View style={{ flexDirection: 'row' }}>
          {days === 0 && hours === 0 && minutes === 0 && seconds === 0 ? (
            <Text style={{ color: color.white }} text={endedMessage} variant="body2Emphasized" />
          ) : (
            <>
              <View style={TIMESTAMP}>
                {hours > 0 ? (
                  <>
                    <Digit value={hours} />
                    <Text style={{ color: color.white }} variant="body2Emphasized" text=":" />
                  </>
                ) : null}
                <Digit value={minutes} />
                <Text style={{ color: color.white }} variant="body2Emphasized" text=":" />
                <Digit value={seconds} />
                <Text text=" " />
                <Text style={{ color: color.white }} variant="body2Emphasized" tx="Common.left" />
              </View>
            </>
          )}
        </View>
      )}
    </View>
  )
})

const CONTAINER: ViewStyle = {
  paddingLeft: 12,
  paddingRight: 12,
  borderColor: color.tint70,
  flexDirection: 'row',
  alignItems: 'center',
  height: 27,
  borderRadius: 100,
}
const TIMESTAMP: ViewStyle = {
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
}
